import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Accordion, AccordionVariants } from '@components/atoms/Accordion';
import { OrderStateBadge } from '@components/molecules/badges/OrderStateBadge';
import { OrderDetailAccordionButton } from '@components/molecules/OrderDetailAccordionButton';
import { OrderHistoryModal } from '@components/molecules/OrderHistoryModal';
import { OrderHistoryTable } from '@components/molecules/OrderHistoryTable';
import { OrderProductsTable } from '@components/molecules/OrderProductsTable';
import { OrderBillingTable } from '@components/molecules/OrderBillingTable';
import { Button, ButtonMode } from '@components/atoms/Button';
import { BookLockerCompartmentsModal, DeleteProductModal } from './modals';
import { DeliveryTable, PaymentsTable } from './tables';
import { InputErrors, OrderDeliveryKeys, OrderState } from '@types';
import { formatDate, formatPhoneNumber, formatPrice, formatTime } from '@utils/format';
import { useGetMyStoresOrderDetail } from '@services/useGetMyStoresOrderDetail';
import { useGenerateShippingLabel } from '@services/useGenerateShippingLabel';
import { useDownloadShippingLabel } from '@services/useDownloadShippingLabel';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import styles from './ManageOrderDetail.module.css';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Notification } from '@components/molecules/Notification';
import { useCancelStoreOrder } from '@services/useCancelStoreOrder';
import { ManagementCard } from '@components/molecules/ManagementCard';

export const ManageOrderDetail: FC = () => {
    const { order_uuid, store_uuid } = useParams();
    const { myStoresOrderDetail, isLoadingMyStoresOrderDetail, setMyStoresOrderDetail } = useData();
    const { request: requestMyStoresOrderDetail } = useGetMyStoresOrderDetail();
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestDownloadShippingLabel,
        loading: isLoadingDownloadShippingLabel,
        error: errorDownloadShippingLabel,
        lastResponseTime: lastDownloadShippingLabel,
    } = useDownloadShippingLabel();
    const {
        request: requestCancelStoreOrder,
        success: successCancel,
        errorMessage: errorCancel,
        lastResponseTime: lastCancel,
    } = useCancelStoreOrder();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-order-detail-component.title'),
        actions: t('manage-order-detail-component.actions.label'),
        // delivered: t('manage-order-detail-component.actions.delivered'),
        // payment: t('manage-order-detail-component.actions.add-payment'),
        // invoice: t('manage-order-detail-component.actions.add-invoice'),
        cancel: t('manage-order-detail-component.actions.cancel-order'),
        productsTitle: t('manage-order-detail-component.products.title'),
        billingTitle: t('manage-order-detail-component.billing.title'),
        deliveryTitle: t('manage-order-detail-component.delivery.title'),
        paymentsTitle: t('manage-order-detail-component.payments.title'),
        historyTitle: t('manage-order-detail-component.history.title'),
        billingOptionAddPopoverTitle: t('manage-order-detail-component.billing.add.popover.title'),
        billingOptionAddPopoverText: t('manage-order-detail-component.billing.add.popover.text'),
        orderTitle: t('manage-order-detail-component.order-card.title'),
        client: t('manage-order-detail-component.order-card.client'),
        date: t('manage-order-detail-component.order-card.date'),
        total: t('manage-order-detail-component.order-card.total'),
        discount: t('manage-order-detail-component.order-card.discount'),
        address: t('manage-order-detail-component.delivery-card.address.label'),
        // delivery: t('manage-order-detail-component.delivery-card.delivery'),
        condition: t('manage-order-detail-component.delivery-card.condition'),
        deliveryDigital: t('manage-order-detail-component.delivery-card.address.digital'),
        deliveryAtAddress: t('manage-order-detail-component.delivery-card.address.at-address'),
        deliveryAtStore: t('manage-order-detail-component.delivery-card.address.at-store'),
        deliveryAtPickupSpot: t('manage-order-detail-component.delivery-card.address.at-pickup-spot'),
        deliveryAtLocker: t('manage-order-detail-component.delivery-card.address.at-locker'),
        shippingLabel: t('manage-order-detail-component.delivery-card.shipping-label.label'),
        shippingLabelDownload: t('manage-order-detail-component.delivery-card.shipping-label.download.button'),
        pickupDate: (date: string) =>
            t('manage-order-detail-component.delivery-card.shipping-label.pickup-date.text', {
                date: date,
                interpolation: { escapeValue: false },
            }),
        pickupTime: (time: string) =>
            t('manage-order-detail-component.delivery-card.shipping-label.pickup-time.text', { time: time }),
        lockerCompartmentsBooking: t('manage-order-detail-component.delivery-card.locker-booking'),
        lockerReservations: t('manage-order-detail-component.delivery-card.locker-reservations'),
    };

    // fetch order detail info
    useEffect(() => {
        requestMyStoresOrderDetail({ order_uuid: order_uuid || '', store_uuid: store_uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps

        // when unmounting page, reset results
        // to prevent showing old results when returning to this component
        return () => {
            setMyStoresOrderDetail(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (lastDownloadShippingLabel && errorDownloadShippingLabel) {
            showDefaultNotification({ type: 'error', message: errorDownloadShippingLabel });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastDownloadShippingLabel]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [editBillingModal, setEditBillingModal] = useState<{ billId: string | null; show: boolean }>({
        billId: null,
        show: false,
    });

    const [viewHistoryModal, setViewHistoryModal] = useState<{ logId: string | null; show: boolean }>({
        logId: null,
        show: false,
    });

    const [viewBookLockerCompartmentsModal, setViewBookLockerCompartmentsModal] = useState<boolean>(false);

    // if null, hide modal
    const [deleteProductModal, setDeleteProductModal] = useState<string | null>(null);

    const delivery = myStoresOrderDetail?.data?.delivery;

    const deliveryTranslations: Record<OrderDeliveryKeys, string> = {
        locker: translations.deliveryAtLocker,
        shipToPickupSpot: translations.deliveryAtPickupSpot,
        pickupAtStore: translations.deliveryAtStore,
        shipToAddress: translations.deliveryAtAddress,
        digitalDelivery: translations.deliveryDigital,
    };

    const deliveryKey = Object.keys(deliveryTranslations).find((key) => {
        const value = delivery?.[key as OrderDeliveryKeys];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return Boolean(value);
    });

    const deliveryType = deliveryKey ? deliveryTranslations[deliveryKey as OrderDeliveryKeys] : '';

    const handleOnClickDownloadShippingLabel = () => {
        requestDownloadShippingLabel({ order_uuid: order_uuid || '', store_uuid: store_uuid || '' });
    };

    const canGenerateShippingLabel =
        (delivery?.shipToPickupSpot || delivery?.shipToAddress) &&
        delivery?.shippingLabel !== null &&
        delivery?.shippingLabel !== undefined;

    const billingAddress = myStoresOrderDetail?.data?.billingAddress;

    const billingAddressText = billingAddress
        ? `${billingAddress?.recipient_first_name} ${billingAddress?.recipient_last_name}` +
          `\nNIF: ${billingAddress?.fiscal_number || '-'}` +
          `\n${billingAddress.address_line1}` +
          `\n${billingAddress.address_line2}` +
          `\n${billingAddress.postal_code}` +
          `\n${billingAddress.municipality}` +
          `\n${billingAddress.region?.name}`
        : '';

    const client = myStoresOrderDetail?.data?.client;

    const clientContent = client ? (
        <div className="d-flex flex-column gap-2">
            {client.name && (
                <Typography classes="mb-0" fontSize="16">
                    {client.name}
                </Typography>
            )}
            {client.phone && (
                <Typography classes="mb-0" fontSize="14">
                    {formatPhoneNumber(client.phone)}
                </Typography>
            )}
            {client.email && (
                <Typography classes="mb-0" fontSize="14">
                    {client.email}
                </Typography>
            )}
        </div>
    ) : (
        ''
    );

    useEffect(() => {
        if (lastCancel) {
            if (errorCancel) {
                showDefaultNotification({ type: 'error', message: errorCancel });
            } else {
                showDefaultNotification({ type: 'success', message: successCancel || '' });
                requestMyStoresOrderDetail({ order_uuid: order_uuid || '', store_uuid: store_uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCancel]);

    const bookedOrderItemUuids = (myStoresOrderDetail?.data?.delivery?.lockerReservations || [])
        .map((item) => item.orderItems)
        .flat()
        .map((item) => item?.order_item_uuid || '');

    // only products that are not associated with a locker booking yet
    const orderItemsToBookOnLocker = (myStoresOrderDetail?.data?.products || []).filter(
        (item) => !bookedOrderItemUuids.includes(item.order_item_uuid || '')
    );

    const showBookLockerCompartmentButton =
        // paid order
        myStoresOrderDetail?.data?.status === 'paid' &&
        // order set for locker delivery
        myStoresOrderDetail?.data?.delivery?.locker &&
        // if there are any order items yet to assign to a locker compartment
        orderItemsToBookOnLocker.length > 0;

    const showLockerReservations =
        // paid order
        myStoresOrderDetail?.data?.status === 'paid' &&
        // order set for locker delivery
        myStoresOrderDetail?.data?.delivery?.locker &&
        // if there are any no more order items to assign to a locker compartment
        orderItemsToBookOnLocker.length === 0;

    return (
        <MyAccountContainer
            title={translations.title}
            overrideOnDesktop={
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <Typography classes="mb-2" color="muted" fontSize="16">
                        <>
                            {translations.title} / {myStoresOrderDetail?.data?.client?.name} /{' '}
                            <Typography tagVariant={TypographyTagVariants.span} fontWeight="800">
                                {myStoresOrderDetail?.data?.order_code}
                            </Typography>
                        </>
                    </Typography>

                    {!isLoadingMyStoresOrderDetail &&
                        (myStoresOrderDetail?.data?.status === 'pending' || myStoresOrderDetail?.data?.status === 'paid') && (
                            <Button
                                mode={ButtonMode.primary}
                                label={translations.actions}
                                dropdown={{
                                    side: 'right',
                                    options: [
                                        /*{ label: translations.delivered, icon: 'fa-light fa-hand-holding-box' },
                                        { label: translations.payment, icon: 'fa-light fa-circle-euro' },
                                        { label: translations.invoice, icon: 'fa-light fa-circle-plus' },*/
                                        {
                                            label: translations.cancel,
                                            icon: 'fa-light fa-xmark',
                                            onClick: () =>
                                                requestCancelStoreOrder({ order_uuid: order_uuid, store_uuid: store_uuid }),
                                        },
                                    ],
                                }}
                            />
                        )}
                </div>
            }>
            <>
                <>
                    {myStoresOrderDetail?.data?.cancellationWarning && (
                        <Notification
                            type="warning"
                            message={myStoresOrderDetail?.data?.cancellationWarning}
                            borderRadiusStyle="rounded"
                            size="full-width"
                        />
                    )}
                </>
                <div className="row g-4 mt-4 mb-4" data-testid="order-management">
                    <div className="col-12 col-md-6">
                        <ManagementCard
                            title={`${translations.orderTitle} - ${myStoresOrderDetail?.data?.order_code}`}
                            content={[
                                { label: translations.client, content: clientContent },
                                {
                                    label: translations.date,
                                    content: myStoresOrderDetail?.data?.creation_date ? (
                                        <>
                                            {formatDate(myStoresOrderDetail.data.creation_date)}
                                            <Typography tagVariant={TypographyTagVariants.span} classes="text-muted ms-2">
                                                <>({formatTime(myStoresOrderDetail?.data?.creation_date)})</>
                                            </Typography>
                                        </>
                                    ) : (
                                        <></>
                                    ),
                                },
                                { label: translations.total, content: formatPrice(myStoresOrderDetail?.data?.total) },
                                myStoresOrderDetail?.data?.discount
                                    ? {
                                          label: translations.discount,
                                          content: formatPrice(myStoresOrderDetail?.data?.discount),
                                      }
                                    : undefined,
                                {
                                    label: translations.condition,
                                    content: (
                                        <OrderStateBadge
                                            state={OrderState[myStoresOrderDetail?.data?.status as keyof typeof OrderState]}
                                        />
                                    ),
                                },
                            ].filter((item) => item !== undefined)}
                        />
                    </div>
                    <div className="col-12 col-md-6">
                        <ManagementCard
                            title={translations.deliveryTitle}
                            content={[
                                { label: translations.address, content: deliveryType },
                                canGenerateShippingLabel
                                    ? {
                                          label: translations.shippingLabel,
                                          content: myStoresOrderDetail ? (
                                              <>
                                                  <GenerateShippingLabelForm
                                                      show={!myStoresOrderDetail?.data?.delivery?.shippingLabel}
                                                  />
                                                  {myStoresOrderDetail?.data?.delivery?.shippingLabel && (
                                                      <Button
                                                          label={translations.shippingLabelDownload}
                                                          mode={ButtonMode.primary}
                                                          onClick={handleOnClickDownloadShippingLabel}
                                                          disabled={isLoadingDownloadShippingLabel}
                                                      />
                                                  )}
                                              </>
                                          ) : (
                                              <></>
                                          ),
                                      }
                                    : undefined,
                                myStoresOrderDetail?.data?.status === 'paid' && myStoresOrderDetail?.data?.delivery?.pickupDate
                                    ? {
                                          label: '',
                                          content: (
                                              <Typography fontSize="14">
                                                  <>
                                                      {translations.pickupDate(
                                                          formatDate(myStoresOrderDetail?.data?.delivery?.pickupDate || '')
                                                      ) +
                                                          ' ' +
                                                          translations.pickupTime(
                                                              myStoresOrderDetail?.data?.delivery?.pickupTime || ''
                                                          )}
                                                  </>
                                              </Typography>
                                          ),
                                      }
                                    : undefined,
                                showBookLockerCompartmentButton
                                    ? {
                                          label: '',
                                          content: (
                                              <Button
                                                  label={translations.lockerCompartmentsBooking}
                                                  mode={ButtonMode.primary}
                                                  onClick={() => setViewBookLockerCompartmentsModal(true)}
                                              />
                                          ),
                                      }
                                    : undefined,
                                showLockerReservations
                                    ? {
                                          label: translations.lockerReservations,
                                          content: (
                                              <>
                                                  {(myStoresOrderDetail?.data?.delivery?.lockerReservations || []).map(
                                                      (reservation) => (
                                                          <p key={reservation.reservation_number}>
                                                              {reservation.reservation_number}
                                                          </p>
                                                      )
                                                  )}
                                              </>
                                          ),
                                      }
                                    : undefined,
                            ].filter((item) => item !== undefined)}
                        />
                    </div>
                </div>

                <Accordion
                    variant={AccordionVariants.variant2}
                    items={[
                        {
                            id: 'collapseProducts',
                            buttonContent: (
                                <OrderDetailAccordionButton
                                    title={translations.productsTitle}
                                    itemsCount={myStoresOrderDetail?.data?.nProducts}
                                    total={myStoresOrderDetail?.data?.productsValue}
                                />
                            ),
                            bodyContent: (
                                <OrderProductsTable
                                    products={
                                        myStoresOrderDetail?.data?.products?.map((item) => {
                                            return {
                                                uuid: item.product_uuid || '',
                                                name: item.name || '',
                                                attributes: item.attributes || [],
                                                quantity: item.quantity || 0,
                                                price: item.price || 0,
                                            };
                                        }) || []
                                    }
                                />
                            ),
                        },
                        /*{
                            id: 'collapseBills',
                            buttonContent: (
                                <OrderDetailAccordionButton
                                    title={translations.billingTitle}
                                    itemsCount={bills.list.length}
                                    total={bills.total}
                                />
                            ),
                            buttonOptions: (
                                <>
                                    {
                                        // popover + add billing info
                                    }
                                    <button
                                        className="text-muted"
                                        onClick={() => setEditBillingModal({ billId: null, show: true })}>
                                        <PopOver
                                            title={translations.billingOptionAddPopoverTitle}
                                            text={translations.billingOptionAddPopoverText}
                                            trigger="hover"
                                            placement="bottom">
                                            <i className="fa-light fa-circle-plus" />
                                        </PopOver>
                                    </button>
                                </>
                            ),
                            bodyContent: (
                                <BillingTable
                                    bills={bills.list}
                                    showEditModal={(id) => setEditBillingModal({ billId: id, show: true })}
                                />
                            ),
                        }*/ {
                            id: 'collapseDelivery',
                            buttonContent: (
                                <OrderDetailAccordionButton
                                    title={translations.deliveryTitle}
                                    itemsCount={
                                        myStoresOrderDetail?.data?.delivery?.locker
                                            ? (myStoresOrderDetail.data.delivery.lockerReservations || []).length
                                            : undefined
                                    }
                                />
                            ),
                            bodyContent: <DeliveryTable orderUuid={order_uuid || ''} storeUuid={store_uuid || ''} />,
                        },
                        {
                            id: 'collapseBilling',
                            buttonContent: <OrderDetailAccordionButton title={translations.billingTitle} />,
                            bodyContent: <OrderBillingTable address={billingAddressText} />,
                        },
                        {
                            id: 'collapsePayments',
                            buttonContent: (
                                <OrderDetailAccordionButton
                                    title={translations.paymentsTitle}
                                    total={myStoresOrderDetail?.data?.total}
                                />
                            ),
                            buttonOptions: (
                                <>
                                    {myStoresOrderDetail?.data?.paymentMethod?.image?.src && (
                                        <img
                                            src={myStoresOrderDetail?.data?.paymentMethod?.image?.src}
                                            alt={myStoresOrderDetail?.data?.paymentMethod?.name}
                                        />
                                    )}
                                </>
                            ),
                            bodyContent: (
                                <PaymentsTable
                                    name={myStoresOrderDetail?.data?.paymentMethod?.name || ''}
                                    method={myStoresOrderDetail?.data?.paymentMethod?.method || ''}
                                    multibancoEntity={myStoresOrderDetail?.data?.paymentMethod?.multibancoEntity || ''}
                                    multibancoReference={myStoresOrderDetail?.data?.paymentMethod?.multibancoReference || ''}
                                    phoneNumber={myStoresOrderDetail?.data?.paymentMethod?.paymentPhoneNumber}
                                />
                            ),
                        },
                        {
                            id: 'collapseHistory',
                            buttonContent: <OrderDetailAccordionButton title={translations.historyTitle} />,
                            buttonOptions: (
                                <>
                                    {/* view all logs */}
                                    <button
                                        className="text-muted"
                                        onClick={() => setViewHistoryModal({ logId: null, show: true })}>
                                        <i className="fa-light fa-clock-rotate-left" />
                                    </button>
                                </>
                            ),
                            bodyContent: (
                                <OrderHistoryTable
                                    logs={
                                        myStoresOrderDetail?.data?.logs?.map((item, index) => {
                                            return {
                                                id: `${index}`,
                                                date: item.datetime || '',
                                                amount: `${item.amount || ''}`,
                                                notes: item.description || '',
                                                store: item.store || '',
                                            };
                                        }) || []
                                    }
                                    showViewModal={(id) => setViewHistoryModal({ logId: id, show: true })}
                                />
                            ),
                        },
                    ]}
                    customClasses={{
                        accordion: styles.accordion,
                    }}
                />

                {/*<EditBillingModal
                    billId={editBillingModal.billId}
                    bills={bills.list}
                    show={editBillingModal.show}
                    onClose={() => setEditBillingModal({ billId: null, show: false })}
                />*/}
                <OrderHistoryModal
                    logId={viewHistoryModal.logId}
                    logs={
                        myStoresOrderDetail?.data?.logs?.map((item, index) => {
                            return {
                                id: `${index}`,
                                date: item.datetime || '',
                                amount: `${item.amount || ''}`,
                                notes: item.description || '',
                                store: item.store || '',
                            };
                        }) || []
                    }
                    show={viewHistoryModal.show}
                    onClose={() => setViewHistoryModal({ logId: null, show: false })}
                />
                <BookLockerCompartmentsModal
                    orderUuid={order_uuid || ''}
                    storeUuid={store_uuid || ''}
                    products={orderItemsToBookOnLocker.map((item) => {
                        return {
                            orderItemUuid: item.order_item_uuid || '',
                            name: item.name || '',
                            price: item.price,
                            quantity: item.quantity,
                            attributes: item.attributes || [],
                        };
                    })}
                    show={viewBookLockerCompartmentsModal}
                    onClose={() => setViewBookLockerCompartmentsModal(false)}
                />
                <DeleteProductModal
                    productId={deleteProductModal}
                    show={deleteProductModal !== null}
                    onClose={() => setDeleteProductModal(null)}
                />
            </>
        </MyAccountContainer>
    );
};

interface ShippingLabelFormErrors {
    height: InputErrors;
    width: InputErrors;
    length: InputErrors;
    phoneNumber: InputErrors;
}

interface GenerateShippingLabelFormProps {
    show: boolean;
}

const GenerateShippingLabelForm: FC<GenerateShippingLabelFormProps> = ({ show }) => {
    const { order_uuid, store_uuid } = useParams();
    const { allSettings } = useData();
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestGenerateShippingLabel,
        loading,
        errorMessage,
        validationErrors,
        success: successMessage,
        lastResponseTime: lastGenerateShippingLabel,
    } = useGenerateShippingLabel();
    const { t } = useTranslation();

    const translations = {
        shippingLabelGenerate: t('manage-order-detail-component.delivery-card.shipping-label.generate.button'),
        shippingLabelGenerating: t('manage-order-detail-component.delivery-card.shipping-label.generating'),
        heightLabel: t('manage-order-detail-component.delivery-card.shipping-label.height.label'),
        heightRequiredError: t('manage-order-detail-component.delivery-card.shipping-label.height.required-error'),
        widthLabel: t('manage-order-detail-component.delivery-card.shipping-label.width.label'),
        widthRequiredError: t('manage-order-detail-component.delivery-card.shipping-label.width.required-error'),
        lengthLabel: t('manage-order-detail-component.delivery-card.shipping-label.length.label'),
        lengthRequiredError: t('manage-order-detail-component.delivery-card.shipping-label.length.required-error'),
        phoneLabel: t('manage-order-detail-component.delivery-card.shipping-label.phone.label'),
    };

    const [expandForm, setExpandForm] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);
    const [height, setHeight] = useState<string>('');
    const [width, setWidth] = useState<string>('');
    const [length, setLength] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<ShippingLabelFormErrors>({
        height: [],
        width: [],
        length: [],
        phoneNumber: [],
    });

    const handleOnClickExpandShippingLabelForm = () => {
        setExpandForm(true);
    };

    const handleOnClickGenerateShippingLabel = () => {
        const errors = validateForm();

        if (
            errors.height.length === 0 &&
            errors.width.length === 0 &&
            errors.length.length === 0 &&
            errors.phoneNumber.length === 0
        ) {
            if (allSettings?.data?.shippingMethod === 'CTT') {
                formRef?.current?.requestSubmit();
            } else {
                submitForm();
            }
        }
    };

    const validateForm: () => ShippingLabelFormErrors = () => {
        const newErrors: ShippingLabelFormErrors = {
            height: [],
            width: [],
            length: [],
            phoneNumber: [],
        };

        if (allSettings?.data?.shippingMethod === 'CTT') {
            !height && newErrors.height.push(translations.heightRequiredError);
            !width && newErrors.width.push(translations.widthRequiredError);
            !length && newErrors.length.push(translations.lengthRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        requestGenerateShippingLabel(
            {
                order_uuid: order_uuid || '',
                store_uuid: store_uuid || '',
            },
            {
                height: Number(height) || 0,
                width: Number(width) || 0,
                length: Number(length) || 0,
                phone_number: phoneNumber.replace(/\s+/g, ''),
            }
        );
    };

    // if generation of shipping label returned successfully, dispatch success notification
    // if not, dispatch error notification
    useEffect(() => {
        if (lastGenerateShippingLabel) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    height: validationErrors?.height || [],
                    width: validationErrors?.width || [],
                    length: validationErrors?.length || [],
                    phoneNumber: validationErrors?.phone_number || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successMessage || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastGenerateShippingLabel]);

    return (
        <>
            {show ? (
                <>
                    {allSettings?.data?.shippingMethod === 'CTT' ? (
                        <Form formRef={formRef} onSubmit={() => submitForm()}>
                            <>
                                {expandForm && (
                                    <div className="row">
                                        <div className="col-12 mb-2">
                                            <Input
                                                type={InputTypes.text}
                                                variant={InputVariants.labeledInput}
                                                label={`${translations.heightLabel}*`}
                                                value={`${height || ''}`}
                                                onChange={(value) => setHeight(value)}
                                                disabled={loading}
                                                state={errors.height.length > 0 ? InputStates.error : undefined}
                                                errorMessages={errors.height}
                                            />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <Input
                                                type={InputTypes.text}
                                                variant={InputVariants.labeledInput}
                                                label={`${translations.widthLabel}*`}
                                                value={`${width || ''}`}
                                                onChange={(value) => setWidth(value)}
                                                disabled={loading}
                                                state={errors.width.length > 0 ? InputStates.error : undefined}
                                                errorMessages={errors.width}
                                            />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <Input
                                                type={InputTypes.text}
                                                variant={InputVariants.labeledInput}
                                                label={`${translations.lengthLabel}*`}
                                                value={`${length || ''}`}
                                                onChange={(value) => setLength(value)}
                                                disabled={loading}
                                                state={errors.length.length > 0 ? InputStates.error : undefined}
                                                errorMessages={errors.length}
                                            />
                                        </div>
                                        <div className="col-12 mb-2">
                                            <Input
                                                type={InputTypes.text}
                                                variant={InputVariants.labeledInput}
                                                label={translations.phoneLabel}
                                                value={phoneNumber}
                                                placeholder="210 000 000"
                                                onChange={(value) => setPhoneNumber(value)}
                                                disabled={loading}
                                                state={errors.phoneNumber.length > 0 ? InputStates.error : undefined}
                                                errorMessages={errors.phoneNumber}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                            <Button
                                label={translations.shippingLabelGenerate}
                                mode={ButtonMode.primary}
                                onClick={expandForm ? handleOnClickGenerateShippingLabel : handleOnClickExpandShippingLabelForm}
                                disabled={loading}
                            />
                            <>{loading && <Typography fontSize="14">{translations.shippingLabelGenerating}</Typography>}</>
                        </Form>
                    ) : (
                        <>
                            <Button
                                label={translations.shippingLabelGenerate}
                                mode={ButtonMode.primary}
                                onClick={handleOnClickGenerateShippingLabel}
                                disabled={loading}
                            />
                            <>{loading && <Typography fontSize="14">{translations.shippingLabelGenerating}</Typography>}</>
                        </>
                    )}
                </>
            ) : (
                <></>
            )}
        </>
    );
};
